'use client';
/* eslint-disable @next/next/no-img-element */
import React, { useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { countryFlagSrc, getCountryName } from '../../helpers/countries';
import styles from './CountryFlags.module.css';

interface CountryFlagsProps {
  countries: string[];
  style?: React.CSSProperties;
  maxVisible?: number;
  flagSize?: {
    width: string;
    height: string;
  };
}

export const CountryFlags: React.FC<CountryFlagsProps> = ({
  countries,
  style,
  maxVisible = 4,
  flagSize = { width: '16px', height: '12px' }
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!tooltipOpen || !wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    setTooltipPos({
      top: rect.top,
      left: rect.left + rect.width / 2,
    });
  }, [tooltipOpen]);

  if (!countries || countries.length === 0) {
    return null;
  }

  const visibleCountries = countries.slice(0, maxVisible);
  const remainingCount = countries.length - maxVisible;
  const hasOverflow = remainingCount > 0;

  const renderFlag = (country: string, index: number) => {
    const countryCode = country?.toUpperCase();
    
    if (countryCode === 'WW') {
      return (
        <span key={`ww-${index}`} style={{ fontSize: '12px' }} title={getCountryName('WW')}>
          🌍
        </span>
      );
    }

    if (countryCode) {
      return (
        <img
          key={countryCode}
          src={countryFlagSrc(countryCode)}
          alt={getCountryName(countryCode)}
          loading="lazy"
          width={parseInt(flagSize.width)}
          height={parseInt(flagSize.height)}
          style={{
            width: flagSize.width,
            height: flagSize.height,
          }}
          title={getCountryName(countryCode)}
        />
      );
    }

    return (
      <span key={`fallback-${index}`} style={{ fontSize: '12px' }}>
        🌍
      </span>
    );
  };

  const renderTooltipContent = () => (
    <div className={styles.tooltipContent}>
      {countries.map((country, index) => {
        const countryCode = country?.toUpperCase();
        const countryName = getCountryName(countryCode);
        return (
          <div key={`tooltip-${countryCode}-${index}`} className={styles.tooltipFlag}>
            {renderFlag(country, index)}
            {countryName}
          </div>
        );
      })}
    </div>
  );

  const handleMouseEnter = () => {
    if (countries.length > 1) {
      setTooltipOpen(true);
    }
  };

  const handleMouseLeave = () => {
    setTooltipOpen(false);
  };

  const showTooltip = countries.length > 1 && tooltipOpen && tooltipPos;

  return (
    <div
      ref={wrapperRef}
      className={styles.tooltipWrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={styles.stackedFlagsContainer}
        ref={containerRef}
        style={style}
      >
        {visibleCountries.map((country, index) => (
          <span
            key={`${country}-${index}`}
            className={styles.flagItem}
            style={{ zIndex: 10 - index }}
          >
            {renderFlag(country, index)}
          </span>
        ))}

        {hasOverflow && (
          <span
            className={`${styles.flagItem} ${styles.overflow}`}
            style={{ zIndex: 10 - visibleCountries.length }}
          >
            +{remainingCount}
          </span>
        )}
      </div>

      {showTooltip && typeof document !== 'undefined' && createPortal(
        <div
          className={`${styles.tooltip} ${styles.tooltipPortal} ${styles.visible}`}
          style={{ top: tooltipPos.top, left: tooltipPos.left }}
        >
          {renderTooltipContent()}
        </div>,
        document.body,
      )}
    </div>
  );
};
